import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { parseISO } from 'date-fns';
import apiClient from '../../utils/axios';
import { fcmTokenManager } from '../../utils/fcm';
import { logEvent, setAnalyticsUser } from '../../utils/analytics';

// 創建認證上下文
const AuthContext = createContext();

// 認證提供者元件
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authAttempted, setAuthAttempted] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 檢查是否需要填寫附加信息
  const needsAdditionalInfo = useCallback((userData) => {
    const currentUser = userData || user;
    // 如果用戶有一個特定的標記表示已經提交過資料，也返回 false
    if (localStorage.getItem('profile-submitted') === 'true') {
      return false;
    }
    return currentUser && !currentUser.isActive && (!currentUser.additionalInfo || currentUser.additionalInfo.trim() === '');
  }, [user]);

  // 檢查訂閱是否過期
  const isSubscriptionExpired = useCallback((userData) => {
    const currentUser = userData || user;
    if (!currentUser || !currentUser.expireDate || currentUser.expireDate === 'permanent') {
      return false; // 沒有到期日或是永久訂閱
    }

    try {
      const expireDate = parseISO(currentUser.expireDate);
      return expireDate < new Date();
    } catch (error) {
      console.error('解析到期日錯誤:', error);
      return false; // 解析錯誤時預設為未過期
    }
  }, [user]);

  // 初始化並檢查認證狀態
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      try {
        await fetchUserInfo();
      } catch (err) {
        console.error('認證初始化錯誤:', err);
        if (err.response && err.response.status === 401) {
          setIsAuthenticated(false);
          setUser(null);
        } else {
          setError('認證初始化失敗');
        }
      } finally {
        setLoading(false);
        setAuthAttempted(true);
      }
    };

    initAuth();
  }, []);

  // 同步 GA 的 user_id 與使用者屬性
  useEffect(() => {
    if (!authAttempted) return;
    setAnalyticsUser(user, user ? isSubscriptionExpired(user) : false);
  }, [authAttempted, user, isSubscriptionExpired]);

  // 檢查用戶狀態並重定向到適當頁面
  useEffect(() => {
    if (!loading && isAuthenticated && user) {
      const currentPath = location.pathname;

      // 判斷用戶是否需要補充資料
      if (needsAdditionalInfo(user) && currentPath !== '/complete-profile') {
        navigate('/complete-profile');
      }
      // 判斷用戶是否已提交資料但尚未審核通過
      else if (!user.isActive && user.additionalInfo) {
        // 檢查當前路徑，允許瀏覽首頁、數據頁和等待審核頁面
        const restrictedPaths = ['/messages', '/message', '/member'];
        const adminPath = currentPath.startsWith('/admin');

        if (restrictedPaths.some(path => currentPath.startsWith(path)) || adminPath) {
          navigate('/pending');
        }
      }
      // 判斷用戶訂閱是否已過期
      else if (isSubscriptionExpired(user)) {
        const allowedPaths = ['/', '/expired', '/callback', '/admin', '/admin/users', '/admin/access', '/admin/subscriptions'];
        const isAdminRoute = currentPath.startsWith('/admin');

        if (!user.isAdmin && !allowedPaths.includes(currentPath) && !isAdminRoute) {
          navigate('/expired');
        }
      }
    }
  }, [loading, isAuthenticated, user, navigate, location.pathname, isSubscriptionExpired, needsAdditionalInfo]);

  // 獲取用戶信息
  const fetchUserInfo = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/users/me');
      setUser(response.data);
      setIsAuthenticated(true);
      return response.data;
    } catch (err) {
      console.error('獲取用戶信息錯誤:', err);
      if (err.response?.status !== 401) {
        setError('獲取用戶信息失敗: ' + err.message);
      }
      setIsAuthenticated(false);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // 獲取 Authentik OIDC 登入 URL 並啟動登入流程
  const login = async () => {
    logEvent('login_start', { method: 'oidc' });
    try {
      const response = await apiClient.get('/auth/login');
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Login failed:', err);
      setError('登入失敗，請重試');
    }
  };

  // 處理 OAuth 回調 - 簡化版本，後端已經處理所有驗證
  const handleCallback = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // 檢查 URL 是否有錯誤參數（由後端重導向時帶入）
      const urlParams = new URLSearchParams(window.location.search);
      const errorParam = urlParams.get('error');

      if (errorParam) {
        logEvent('login_failed', { reason: errorParam === 'access_denied' ? 'cancelled' : 'error' });
        let errorMessage = '登入失敗，請重試';

        switch (errorParam) {
          case 'state_mismatch':
            errorMessage = '登入驗證失敗（CSRF），請重新登入';
            break;
          case 'auth_failed':
            errorMessage = '認證失敗，請確認帳號權限';
            break;
          case 'access_denied':
            errorMessage = '您拒絕了授權請求';
            break;
          default:
            errorMessage = `登入錯誤：${errorParam}`;
        }

        setError(errorMessage);
        return false;
      }

      // 獲取用戶信息（後端已經建立 session）
      const userData = await fetchUserInfo();

      if (userData) {
        logEvent('login', { method: 'oidc' });
        if (needsAdditionalInfo(userData)) {
          navigate('/complete-profile');
        } else if (!userData.isActive && userData.additionalInfo) {
          navigate('/pending');
        } else if (isSubscriptionExpired(userData) && !userData.isAdmin) {
          navigate('/expired');
        } else {
          navigate('/');
        }

        return true;
      } else {
        throw new Error('無法獲取用戶信息');
      }
    } catch (err) {
      console.error('處理回調錯誤:', err);
      setError('登入處理失敗: ' + (err.message || '未知錯誤'));
      setIsAuthenticated(false);
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, [navigate, fetchUserInfo, needsAdditionalInfo, isSubscriptionExpired]);

  // 登出
  const logout = async () => {
    logEvent('logout');
    await fcmTokenManager.unregister();
    try {
      await apiClient.delete('/auth/session');
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      navigate('/');
    }
  };

  // 提交附加信息
  const submitAdditionalInfo = async (additionalInfo) => {
    try {
      const response = await apiClient.patch('/users/me', { additionalInfo });
      await fetchUserInfo();
      return {
        success: true,
        message: response.data.message || '資料已提交，等待管理員審核'
      };
    } catch (err) {
      console.error('個人資料提交錯誤:', err);
      return {
        success: false,
        message: err.response?.data?.message || '提交失敗，請稍後再試'
      };
    }
  };

  // 提供上下文數據
  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    authAttempted,
    login,
    logout,
    handleCallback,
    fetchUserInfo,
    needsAdditionalInfo,
    submitAdditionalInfo,
    isSubscriptionExpired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// 使用認證上下文的 hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth 必須在 AuthProvider 內使用');
  }
  return context;
};

export default AuthContext;
